import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import {
  Telescope, Database, Eye, EyeOff, AlertTriangle, Loader2,
  Search, ChevronDown, ChevronRight, BookOpen, Link2Off,
  Building, Activity, FileSearch, ShieldAlert, ShieldOff, Wrench, RotateCcw, CheckCheck,
  CheckCircle2, History,
} from 'lucide-react';

const COLORS = {
  emerald: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40',
  amber:   'bg-amber-500/15 text-amber-300 border-amber-500/40',
  rose:    'bg-rose-500/15 text-rose-300 border-rose-500/40',
  slate:   'bg-slate-500/15 text-slate-300 border-slate-500/40',
};

const CATEGORY_LABELS = {
  finance:     { ar: 'المالية',      en: 'Finance' },
  projects:    { ar: 'المشاريع',     en: 'Projects' },
  procurement: { ar: 'المشتريات',    en: 'Procurement' },
  warehouse:   { ar: 'المستودعات',   en: 'Warehouse' },
  contracts:   { ar: 'العقود',       en: 'Contracts' },
  crm:         { ar: 'العملاء و CRM', en: 'CRM' },
  hr:          { ar: 'الموارد البشرية', en: 'HR' },
  assets:      { ar: 'الأصول والمعدات', en: 'Assets' },
  admin:       { ar: 'الإدارة',      en: 'Administration' },
};

const REASON_LABELS = {
  hidden_missing_company: { ar: 'بدون company_id', en: 'Missing company_id' },
  hidden_dead_company:    { ar: 'company_id غير موجود', en: 'Dead company_id' },
  hidden_soft_deleted:    { ar: 'محذوف منطقياً',     en: 'Soft-deleted' },
  hidden_broken_fk:       { ar: 'مرجع مكسور',        en: 'Broken FK' },
  hidden_missing_id:      { ar: 'بدون id',           en: 'Missing id field' },
};

function HealthPill({ score }) {
  const color = score >= 95 ? 'emerald' : score >= 70 ? 'amber' : 'rose';
  return (
    <span data-testid={`health-pill-${color}`}
      className={`px-2 py-0.5 rounded text-xs font-semibold border ${COLORS[color]}`}>
      {score}%
    </span>
  );
}

function StatCard({ label, label_ar, value, sub, icon: Icon, accent = 'slate', testid }) {
  return (
    <div data-testid={testid}
      className={`rounded-lg border ${COLORS[accent]} p-4 backdrop-blur-sm`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-wide opacity-70">{label}</div>
          <div className="text-[11px] opacity-50">{label_ar}</div>
        </div>
        {Icon && <Icon size={18} className="opacity-60" />}
      </div>
      <div className="mt-2 text-2xl font-mono font-semibold">{value}</div>
      {sub && <div className="text-xs opacity-60 mt-1">{sub}</div>}
    </div>
  );
}

const LegacyRecoveryCenter = ({ user }) => {
  const isAdmin = user?.role?.toLowerCase() === 'admin';
  const [loading, setLoading] = useState(false);
  const [scan, setScan] = useState(null);
  const [orphans, setOrphans] = useState(null);
  const [leaks, setLeaks] = useState(null);
  const [untracked, setUntracked] = useState(null);
  const [selectedPage, setSelectedPage] = useState(null);
  const [detail, setDetail] = useState(null);
  const [activeTab, setActiveTab] = useState('pages');
  const [query, setQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');

  // Phase 1 — Recovery state
  const [recoveryPreview, setRecoveryPreview] = useState(null);
  const [recoveryRunning, setRecoveryRunning] = useState(false);
  const [recoveryResult, setRecoveryResult] = useState(null);
  const [pastRuns, setPastRuns] = useState([]);
  const [snapshotBefore, setSnapshotBefore] = useState(null);
  const [minConfidence, setMinConfidence] = useState(80);
  const [evidenceRequired, setEvidenceRequired] = useState(true);
  // Phase 1C — Dead Company preview
  const [deadCompanyPreview, setDeadCompanyPreview] = useState(null);
  // Phase 1D — Broken FK preview
  const [brokenFkPreview, setBrokenFkPreview] = useState(null);
  // Unified Before/After report
  const [reportBaseline, setReportBaseline] = useState(null);
  const [reportDiff, setReportDiff] = useState(null);
  const [approvalScan, setApprovalScan] = useState(null);
  const [autoApprovePreview, setAutoApprovePreview] = useState(null);
  const [domainContract, setDomainContract] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [s, o, l, u, a, dc] = await Promise.all([
        axiosInstance.post('/legacy-audit/scan?sample_limit=5'),
        axiosInstance.get('/legacy-audit/orphans'),
        axiosInstance.get('/legacy-audit/tenant-leaks'),
        axiosInstance.get('/legacy-audit/untracked'),
        axiosInstance.get('/legacy-audit/hidden-by-approval?sample_limit=2'),
        axiosInstance.get('/legacy-audit/domain-contract'),
      ]);
      setScan(s.data);
      setOrphans(o.data);
      setLeaks(l.data);
      setUntracked(u.data);
      setApprovalScan(a.data);
      setDomainContract(dc.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'فشل المسح / Scan failed');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (isAdmin) fetchAll(); }, [fetchAll, isAdmin]);

  const fetchDetail = useCallback(async (pageId) => {
    setSelectedPage(pageId);
    setDetail(null);
    try {
      const r = await axiosInstance.get(
        `/legacy-audit/page/${pageId}?sample_limit=50`,
      );
      setDetail(r.data);
    } catch (e) {
      toast.error('Failed to load details');
    }
  }, []);

  // ─── Phase 1A — Preview Recovery ──────────────────────────────
  const openRecoveryPreview = useCallback(async () => {
    setRecoveryResult(null);
    setRecoveryPreview('loading');
    try {
      const r = await axiosInstance.post(
        `/legacy-audit/recover/missing-company-id/preview?evidence_required=${evidenceRequired}`,
      );
      setRecoveryPreview(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Preview failed');
      setRecoveryPreview(null);
    }
  }, [evidenceRequired]);

  // ─── Phase 1B — Execute Recovery (with auto re-scan + diff) ────
  const executeRecovery = useCallback(async () => {
    if (!recoveryPreview || recoveryPreview === 'loading') return;
    const willRecover = recoveryPreview.total_recoverable ?? recoveryPreview.total_affected;
    const confirmMsg =
      `سيتم تعديل ${willRecover} سجل\n` +
      `(${recoveryPreview.total_abstained || 0} سجل ممتنع عنه لعدم وجود دليل)\n\n` +
      `لن يتم حذف أي بيانات. يمكن التراجع لاحقاً عبر Rollback.\n\n` +
      `${willRecover} records will be modified ` +
      `(${recoveryPreview.total_abstained || 0} abstained — no evidence).\n` +
      `No data will be deleted. Rollback is available afterwards.\n\nProceed?`;
    if (!window.confirm(confirmMsg)) return;
    setRecoveryRunning(true);
    setSnapshotBefore(scan?.system);
    try {
      const r = await axiosInstance.post(
        `/legacy-audit/recover/missing-company-id/execute` +
        `?min_confidence=${minConfidence}&evidence_required=${evidenceRequired}`,
      );
      // Auto re-scan
      const after = await axiosInstance.post('/legacy-audit/scan?sample_limit=5');
      const leaks = await axiosInstance.get('/legacy-audit/tenant-leaks');
      setScan(after.data);
      setLeaks(leaks.data);
      setRecoveryResult({ run: r.data, after: after.data.system });
      const runs = await axiosInstance.get('/legacy-audit/recovery-runs?limit=20');
      setPastRuns(runs.data.runs || []);
      toast.success(`✅ تم تعديل ${r.data.records_changed} سجل — ${r.data.id.slice(0, 8)}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Execution failed');
    } finally {
      setRecoveryRunning(false);
    }
  }, [recoveryPreview, minConfidence, evidenceRequired, scan]);

  // ─── Rollback ───────────────────────────────────────────────────
  const rollbackRun = useCallback(async (runId) => {
    if (!window.confirm(`Rollback run ${runId.slice(0, 8)}? Every change in this run will be reverted.`)) return;
    try {
      const r = await axiosInstance.post(`/legacy-audit/recovery-runs/${runId}/rollback`);
      toast.success(`Reverted ${r.data.reverted} change(s)`);
      await fetchAll();
      const runs = await axiosInstance.get('/legacy-audit/recovery-runs?limit=20');
      setPastRuns(runs.data.runs || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Rollback failed');
    }
  }, [fetchAll]);

  const loadPastRuns = useCallback(async () => {
    try {
      const r = await axiosInstance.get('/legacy-audit/recovery-runs?limit=20');
      setPastRuns(r.data.runs || []);
    } catch (e) { /* silent */ }
  }, []);

  // ─── Phase 1C — Dead Company preview/execute ───────────────────
  const openDeadCompanyPreview = useCallback(async () => {
    setDeadCompanyPreview('loading');
    try {
      const r = await axiosInstance.post('/legacy-audit/recover/dead-company/preview');
      setDeadCompanyPreview(r.data);
    } catch (e) {
      toast.error('Preview failed');
      setDeadCompanyPreview(null);
    }
  }, []);

  const executeDeadCompany = useCallback(async () => {
    if (!deadCompanyPreview || deadCompanyPreview === 'loading') return;
    const recoverable = deadCompanyPreview.total_auto_recoverable || 0;
    if (!window.confirm(`Re-target ${recoverable} dead-company records (Class A+/A only)? Rollback available.`)) return;
    setRecoveryRunning(true);
    try {
      const r = await axiosInstance.post('/legacy-audit/recover/dead-company/execute?allow_classes=A+,A');
      toast.success(`✅ ${r.data.records_changed} dead-company refs re-targeted`);
      setDeadCompanyPreview(null);
      await fetchAll();
      await loadPastRuns();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Execution failed');
    } finally { setRecoveryRunning(false); }
  }, [deadCompanyPreview, fetchAll, loadPastRuns]);

  // ─── Phase 1D — Broken FK preview/execute ──────────────────────
  const openBrokenFkPreview = useCallback(async () => {
    setBrokenFkPreview('loading');
    try {
      const r = await axiosInstance.post('/legacy-audit/recover/broken-fk/preview');
      setBrokenFkPreview(r.data);
    } catch (e) {
      toast.error('Preview failed');
      setBrokenFkPreview(null);
    }
  }, []);

  const executeBrokenFk = useCallback(async () => {
    if (!brokenFkPreview || brokenFkPreview === 'loading') return;
    const recoverable = brokenFkPreview.total_auto_recoverable || 0;    if (recoverable === 0) {
      toast.info('No A+ class broken FKs — all require manual review');
      return;
    }
    if (!window.confirm(`Re-target ${recoverable} broken FKs (Class A+ exact name match only)? Rollback available.`)) return;
    setRecoveryRunning(true);
    try {
      const r = await axiosInstance.post('/legacy-audit/recover/broken-fk/execute');
      toast.success(`✅ ${r.data.records_changed} broken FKs re-targeted`);
      setBrokenFkPreview(null);
      await fetchAll();
      await loadPastRuns();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Execution failed');
    } finally { setRecoveryRunning(false); }
  }, [brokenFkPreview, fetchAll, loadPastRuns]);

  // ─── Phase 3.1: Quarantine Layer for Class B broken-FK records ───  // Marks them in place (no archive, no delete, no re-target) so the
  // central visibility filter excludes them from BUSINESS layer queries.
  // Fully reversible via /quarantine-runs/{id}/unquarantine.
  const quarantineClassB = useCallback(async () => {
    if (!brokenFkPreview || brokenFkPreview === 'loading') return;
    const manual = (brokenFkPreview.total_broken_links || 0)
                 - (brokenFkPreview.total_auto_recoverable || 0);
    if (manual === 0) {
      toast.info('No Class B records to quarantine');
      return;
    }
    if (!window.confirm(
      `Mark ${manual} Class B broken-FK record${manual === 1 ? '' : 's'} as quarantined?\n\n` +
      `• Records stay in DB (no data loss).\n` +
      `• Excluded from business pages automatically.\n` +
      `• Fully reversible from the Past Runs tab.`,
    )) return;
    setRecoveryRunning(true);
    try {
      const r = await axiosInstance.post('/legacy-audit/quarantine/apply');
      const n = r.data?.records_marked ?? manual;
      toast.success(`🛡️ ${n} record${n === 1 ? '' : 's'} quarantined · run ${(r.data?.id || r.data?.run_id || '').slice(0, 8)}…`);
      setBrokenFkPreview(null);
      await fetchAll();
      await loadPastRuns();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Quarantine failed');
    } finally { setRecoveryRunning(false); }
  }, [brokenFkPreview, fetchAll, loadPastRuns]);

  // ─── Phase 3.2: Auto-Approve Legacy backfill ──────────────────────
  // Targets the LAST class of orphan record: legacy approval_status
  // values that the list endpoints filter out (null / missing / draft /
  // pending / legacy / other). Rejected rows are NEVER touched.
  const openAutoApprovePreview = useCallback(async () => {
    setAutoApprovePreview('loading');
    try {
      const r = await axiosInstance.post(
        '/legacy-audit/recover/auto-approve-legacy/preview?min_age_days=7',
      );
      setAutoApprovePreview(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Preview failed');
      setAutoApprovePreview(null);
    }
  }, []);

  const executeAutoApprove = useCallback(async () => {
    if (!autoApprovePreview || autoApprovePreview === 'loading') return;
    const eligible = autoApprovePreview.total_eligible || 0;
    if (eligible === 0) {
      toast.info('Nothing to auto-approve');
      return;
    }
    if (!window.confirm(
      `Auto-approve ${eligible} legacy record${eligible === 1 ? '' : 's'}?\n\n` +
      `• approval_status will become "approved" + audit metadata.\n` +
      `• Rejected records are NEVER touched.\n` +
      `• Rows newer than 7 days are skipped.\n` +
      `• Fully reversible from Past Runs tab.`,
    )) return;
    setRecoveryRunning(true);
    try {
      const r = await axiosInstance.post(
        '/legacy-audit/recover/auto-approve-legacy/execute?min_age_days=7',
      );
      const n = r.data?.records_changed ?? eligible;
      toast.success(`✅ ${n} record${n === 1 ? '' : 's'} auto-approved · run ${(r.data?.id || '').slice(0, 8)}…`);
      setAutoApprovePreview(null);
      await fetchAll();
      await loadPastRuns();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Execution failed');
    } finally { setRecoveryRunning(false); }
  }, [autoApprovePreview, fetchAll, loadPastRuns]);

  // ─── Unified Before/After report ───────────────────────────────
  const captureBaseline = useCallback(async () => {
    try {
      const r = await axiosInstance.get('/legacy-audit/snapshot');
      setReportBaseline(r.data);
      setReportDiff(null);
      toast.success(`Baseline captured — health ${r.data.system_health}%`);
    } catch (e) {
      toast.error('Failed to capture baseline');
    }
  }, []);

  const generateDiff = useCallback(async () => {
    if (!reportBaseline) {
      toast.warning('Capture baseline first');
      return;
    }
    try {
      const r = await axiosInstance.post('/legacy-audit/diff-report', reportBaseline);
      setReportDiff(r.data);
    } catch (e) {
      toast.error('Diff report failed');
    }
  }, [reportBaseline]);

  useEffect(() => { if (isAdmin) loadPastRuns(); }, [loadPastRuns, isAdmin]);

  const pages = scan?.pages || [];
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return pages
      .filter(p => categoryFilter === 'all' || p.category === categoryFilter)
      .filter(p => !q
        || (p.page_label || '').toLowerCase().includes(q)
        || (p.page_label_ar || '').includes(q)
        || (p.collection || '').toLowerCase().includes(q))
      .sort((a, b) => b.hidden_records - a.hidden_records);
  }, [pages, query, categoryFilter]);

  if (!isAdmin) {
    return (
      <div className="p-8 text-rose-300" data-testid="legacy-audit-no-access">
        <ShieldAlert className="inline mr-2" /> Admin only / للمدير فقط
      </div>
    );
  }

  const system = scan?.system || {};
  const byCat = scan?.by_category || {};

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200 p-6" data-testid="legacy-recovery-center">
      {/* Header */}
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3" data-testid="page-title">
            <Telescope className="text-cyan-400" size={32} />
            <span>Legacy Recovery Center</span>
            <span className="text-lg text-slate-400 font-normal">/ مركز استرجاع البيانات القديمة</span>
          </h1>
          <p className="text-sm text-slate-400 mt-2 max-w-3xl">
            <span dir="rtl" className="block">
              وضع الاكتشاف فقط (Read-Only). لا يتم تعديل أو حذف أي سجل.
              الهدف اكتشاف البيانات الموجودة في قاعدة البيانات ولكنها لا تظهر للمستخدم.
            </span>
            <span className="block mt-1">
              Read-only discovery mode — no record is mutated. Surfaces every row that
              exists in MongoDB but never reaches the UI, plus the reason WHY.
            </span>
          </p>
        </div>
        <button data-testid="rescan-btn" onClick={fetchAll} disabled={loading}
          className="px-4 py-2 bg-cyan-500/20 border border-cyan-500/40 text-cyan-300 rounded hover:bg-cyan-500/30 transition flex items-center gap-2 disabled:opacity-50">
          {loading ? <Loader2 size={16} className="animate-spin" /> : <Activity size={16} />}
          {loading ? 'يفحص…' : 'إعادة الفحص / Re-scan'}
        </button>
      </div>

      {/* System rollup */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <StatCard testid="stat-total" label="Total records" label_ar="إجمالي السجلات"
          value={system.total_records?.toLocaleString() || '—'} icon={Database} accent="slate" />
        <StatCard testid="stat-visible" label="Visible to users" label_ar="ظاهر للمستخدم"
          value={system.visible_records?.toLocaleString() || '—'} icon={Eye} accent="emerald" />
        <StatCard testid="stat-hidden" label="Hidden records" label_ar="مفقود من الواجهة"
          value={system.hidden_records?.toLocaleString() || '—'} icon={EyeOff} accent="rose" />
        <StatCard testid="stat-health" label="System health" label_ar="صحة النظام"
          value={`${system.health_score ?? '—'}%`} icon={Activity}
          accent={system.health_score >= 95 ? 'emerald' : system.health_score >= 70 ? 'amber' : 'rose'} />
        <StatCard testid="stat-pages" label="Pages audited" label_ar="عدد الصفحات"
          value={system.pages_count || '—'} icon={BookOpen} accent="slate" />
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-800 mb-4 flex-wrap">
        {[
          { id: 'pages',     label: 'الصفحات',         icon: BookOpen,  count: pages.length },
          { id: 'leaks',     label: 'تسرّب العميل',    icon: Building,  count: leaks?.total_leaks },
          { id: 'orphans',   label: 'مراجع مكسورة',    icon: Link2Off,  count: orphans?.total_broken_links },
          { id: 'untracked', label: 'مجموعات غير مسجّلة', icon: FileSearch, count: untracked?.total_untracked },
          { id: 'recovery',  label: 'الاسترجاع',        icon: Wrench,    count: pastRuns?.length },
          { id: 'approval',  label: 'مخفي بفلتر الموافقة', icon: ShieldAlert, count: approvalScan?.summary?.total_hidden_by_approval },
          { id: 'domain',    label: 'Domain Contract',  icon: BookOpen, count: domainContract?.states?.length },
        ].map(t => (
          <button key={t.id} data-testid={`tab-${t.id}`} onClick={() => setActiveTab(t.id)}
            className={`px-4 py-2 flex items-center gap-2 border-b-2 transition ${
              activeTab === t.id
                ? 'border-cyan-400 text-cyan-300'
                : 'border-transparent text-slate-400 hover:text-slate-200'}`}>
            <t.icon size={16} />
            <span>{t.label}</span>
            {t.count != null && (
              <span className="text-xs px-1.5 py-0.5 rounded bg-slate-800">{t.count}</span>
            )}
          </button>
        ))}
      </div>

      {/* Pages tab */}
      {activeTab === 'pages' && (
        <div className="space-y-4">
          {/* Filters */}
          <div className="flex gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[220px]">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <input data-testid="page-search" type="text" value={query} onChange={e => setQuery(e.target.value)}
                placeholder="Search pages / ابحث في الصفحات…"
                className="w-full bg-slate-900 border border-slate-800 rounded pl-9 pr-3 py-2 text-sm focus:border-cyan-500 outline-none" />
            </div>
            <select data-testid="category-filter" value={categoryFilter}
              onChange={e => setCategoryFilter(e.target.value)}
              className="bg-slate-900 border border-slate-800 rounded px-3 py-2 text-sm">
              <option value="all">All categories / كل الفئات</option>
              {Object.keys(byCat).map(c => (
                <option key={c} value={c}>{CATEGORY_LABELS[c]?.en || c}</option>
              ))}
            </select>
          </div>

          {/* Category rollup */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
            {Object.entries(byCat).map(([cat, st]) => (
              <button key={cat} data-testid={`cat-card-${cat}`}
                onClick={() => setCategoryFilter(cat === categoryFilter ? 'all' : cat)}
                className={`text-left rounded border p-2 transition ${
                  cat === categoryFilter
                    ? 'border-cyan-500 bg-cyan-500/10'
                    : 'border-slate-800 bg-slate-900/40 hover:border-slate-700'}`}>
                <div className="text-xs uppercase opacity-70">{CATEGORY_LABELS[cat]?.en || cat}</div>
                <div className="text-[10px] opacity-50">{CATEGORY_LABELS[cat]?.ar || ''}</div>
                <div className="mt-1 text-sm font-mono">
                  <span className="text-emerald-300">{st.visible}</span>
                  <span className="opacity-50"> / {st.total}</span>
                </div>
                {st.hidden > 0 && (
                  <div className="text-xs text-rose-300 font-mono">−{st.hidden} hidden</div>
                )}
              </button>
            ))}
          </div>

          {/* Pages table */}
          <div className="rounded border border-slate-800 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-900 text-slate-400 text-xs uppercase">
                <tr>
                  <th className="px-3 py-2 text-left">Page / الصفحة</th>
                  <th className="px-3 py-2 text-left">Collection</th>
                  <th className="px-3 py-2 text-right">DB</th>
                  <th className="px-3 py-2 text-right">Visible</th>
                  <th className="px-3 py-2 text-right">Hidden</th>
                  <th className="px-3 py-2 text-center">Health</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(p => (
                  <tr key={p.page_id} data-testid={`page-row-${p.page_id}`}
                    className="border-t border-slate-800/50 hover:bg-slate-900/40">
                    <td className="px-3 py-2">
                      <div className="font-medium">{p.page_label}</div>
                      <div className="text-xs opacity-60" dir="rtl">{p.page_label_ar}</div>
                      <div className="text-[10px] opacity-40 font-mono">{p.frontend_route}</div>
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">
                      {p.collection}
                      {p.collection_exists === false && (
                        <span className="ml-2 text-rose-400 text-[10px]">(missing)</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right font-mono">{p.total_records.toLocaleString()}</td>
                    <td className="px-3 py-2 text-right font-mono text-emerald-300">{p.visible_records.toLocaleString()}</td>
                    <td className="px-3 py-2 text-right font-mono text-rose-300">
                      {p.hidden_records > 0 ? `−${p.hidden_records.toLocaleString()}` : '0'}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <HealthPill score={p.health_score} />
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button data-testid={`view-detail-${p.page_id}`}
                        onClick={() => fetchDetail(p.page_id)}
                        className="px-2 py-1 text-xs bg-cyan-500/10 border border-cyan-500/30 rounded text-cyan-300 hover:bg-cyan-500/20">
                        Details / تفاصيل
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Tenant leaks tab */}
      {activeTab === 'leaks' && leaks && (
        <div data-testid="leaks-tab" className="rounded border border-slate-800 overflow-hidden">
          <div className="bg-slate-900 px-4 py-3">
            <div className="flex justify-between items-center">
              <div>
                <div className="font-semibold">Tenant Leaks / تسرّب الشركات</div>
                <div className="text-xs text-slate-500">
                  Documents without a valid company_id — {leaks.live_companies} live companies registered
                </div>
              </div>
              <div className="text-2xl font-mono text-rose-300">{leaks.total_leaks?.toLocaleString()}</div>
            </div>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-900/60 text-xs uppercase text-slate-400">
              <tr>
                <th className="px-3 py-2 text-left">Page</th>
                <th className="px-3 py-2 text-left">Collection</th>
                <th className="px-3 py-2 text-right">Missing company_id</th>
                <th className="px-3 py-2 text-right">Dead company_id</th>
              </tr>
            </thead>
            <tbody>
              {(leaks.leaks || []).map(l => (
                <tr key={l.page_id} className="border-t border-slate-800/50">
                  <td className="px-3 py-2">{l.page_label}</td>
                  <td className="px-3 py-2 font-mono text-xs">{l.collection}</td>
                  <td className="px-3 py-2 text-right font-mono text-rose-300">
                    {l.missing_company_id || ''}
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-amber-300">
                    {l.dead_company_id || ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Orphans tab */}
      {activeTab === 'orphans' && orphans && (
        <div data-testid="orphans-tab" className="space-y-3">
          <div className="text-sm text-slate-400">
            {orphans.total_broken_links} broken FK references found across registered pages.
            Records exist but point to a parent that no longer exists.
          </div>
          {(orphans.broken_links || []).map((b, i) => (
            <div key={i} className="rounded border border-slate-800 bg-slate-900/40 p-3">
              <div className="flex justify-between items-start">
                <div>
                  <div className="font-medium">{b.page_label}</div>
                  <div className="text-xs text-slate-500">
                    <span className="font-mono">{b.collection}.{b.broken_field}</span>
                    <span className="mx-2">→</span>
                    <span className="font-mono">{b.target}</span>
                  </div>
                </div>
                <div className="text-xl font-mono text-rose-300">{b.orphan_count}</div>
              </div>
              {b.orphans?.length > 0 && (
                <details className="mt-2">
                  <summary className="text-xs cursor-pointer text-slate-400 hover:text-slate-200">
                    Show first {b.orphans.length} orphans
                  </summary>
                  <pre className="text-xs mt-2 bg-black/30 p-2 rounded overflow-auto max-h-48">
                    {JSON.stringify(b.orphans, null, 2)}
                  </pre>
                </details>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Untracked tab */}
      {activeTab === 'untracked' && untracked && (
        <div data-testid="untracked-tab" className="rounded border border-slate-800 overflow-hidden">
          <div className="bg-slate-900 px-4 py-3 text-sm text-slate-400">
            Collections that contain rows but are NOT mapped to any page in the registry.
            They could be abandoned features, internal scratch tables, or pages we forgot to register.
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-900/60 text-xs uppercase text-slate-400">
              <tr>
                <th className="px-3 py-2 text-left">Collection</th>
                <th className="px-3 py-2 text-right">Rows</th>
                <th className="px-3 py-2 text-left">Sample keys</th>
              </tr>
            </thead>
            <tbody>
              {(untracked.collections || []).map((c, i) => (
                <tr key={i} className="border-t border-slate-800/50">
                  <td className="px-3 py-2 font-mono text-xs">{c.collection}</td>
                  <td className="px-3 py-2 text-right font-mono">{c.row_count}</td>
                  <td className="px-3 py-2 text-xs text-slate-400">{c.sample_keys?.join(', ')}</td>
                </tr>
              ))}
              {(untracked.collections || []).length === 0 && (
                <tr><td colSpan={3} className="px-3 py-6 text-center text-emerald-300">
                  ✓ كل collection معروف / Every collection is registered
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Recovery tab — Phase 1A/1B/1C/1D + Unified Report */}
      {activeTab === 'recovery' && (
        <div data-testid="recovery-tab" className="space-y-4">
          {/* Three recovery cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {/* Missing company_id */}
            <div className="rounded border border-cyan-500/30 bg-cyan-500/5 p-4">
              <div className="text-sm font-semibold flex items-center gap-2">
                <span className={`px-1.5 py-0.5 rounded text-[10px] border ${COLORS.rose}`}>P0</span>
                Missing company_id
              </div>
              <p className="text-xs text-slate-400 mt-2">
                {leaks?.total_leaks ?? '—'} records without a company. Evidence-only mode protects untraceable rows.
              </p>
              <button data-testid="recovery-preview-btn" onClick={openRecoveryPreview}
                className="mt-3 w-full px-3 py-1.5 bg-cyan-500/20 border border-cyan-500/40 text-cyan-300 rounded text-sm hover:bg-cyan-500/30 flex items-center justify-center gap-2">
                <FileSearch size={14} /> Preview & Recover
              </button>
            </div>

            {/* Dead company */}
            <div className="rounded border border-fuchsia-500/30 bg-fuchsia-500/5 p-4">
              <div className="text-sm font-semibold flex items-center gap-2">
                <span className={`px-1.5 py-0.5 rounded text-[10px] border ${COLORS.rose}`}>P0</span>
                Dead Company References
              </div>
              <p className="text-xs text-slate-400 mt-2">
                Records pointing at a deleted tenant. Auto-execute restricted to Class A+ / A only.
              </p>
              <button data-testid="dead-company-preview-btn" onClick={openDeadCompanyPreview}
                className="mt-3 w-full px-3 py-1.5 bg-fuchsia-500/20 border border-fuchsia-500/40 text-fuchsia-300 rounded text-sm hover:bg-fuchsia-500/30 flex items-center justify-center gap-2">
                <FileSearch size={14} /> Preview & Recover
              </button>
            </div>

            {/* Broken FK */}
            <div className="rounded border border-amber-500/30 bg-amber-500/5 p-4">
              <div className="text-sm font-semibold flex items-center gap-2">
                <span className={`px-1.5 py-0.5 rounded text-[10px] border ${COLORS.rose}`}>P0</span>
                Broken Foreign Keys
              </div>
              <p className="text-xs text-slate-400 mt-2">
                {orphans?.total_broken_links ?? '—'} broken links. Three remedies per row (re-target / clear / archive).
              </p>
              <button data-testid="broken-fk-preview-btn" onClick={openBrokenFkPreview}
                className="mt-3 w-full px-3 py-1.5 bg-amber-500/20 border border-amber-500/40 text-amber-300 rounded text-sm hover:bg-amber-500/30 flex items-center justify-center gap-2">
                <FileSearch size={14} /> Preview & Recover
              </button>
            </div>
          </div>

          {/* Unified Before/After Report */}
          <div className="rounded border border-emerald-500/30 bg-emerald-500/5 p-4">
            <div className="flex justify-between items-start mb-3">
              <div>
                <div className="text-sm font-semibold flex items-center gap-2">
                  <CheckCircle2 size={16} className="text-emerald-400" /> Unified Before/After Report
                </div>
                <p className="text-xs text-slate-400 mt-1">
                  1) Capture baseline · 2) Run recoveries · 3) Generate diff to see exactly what changed.
                </p>
              </div>
              <div className="flex gap-2">
                <button data-testid="capture-baseline-btn" onClick={captureBaseline}
                  className="px-3 py-1.5 text-sm bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 rounded">
                  {reportBaseline ? 'Re-capture' : 'Capture Baseline'}
                </button>
                <button data-testid="generate-diff-btn" onClick={generateDiff} disabled={!reportBaseline}
                  className="px-3 py-1.5 text-sm bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 rounded disabled:opacity-30">
                  Generate Diff
                </button>
              </div>
            </div>
            {reportBaseline && !reportDiff && (
              <div className="text-xs text-slate-400">
                Baseline captured at {reportBaseline.captured_at?.slice(0, 19)} —
                health <span className="font-mono">{reportBaseline.system_health}%</span> ·
                hidden <span className="font-mono">{reportBaseline.hidden_records}</span> ·
                leaks <span className="font-mono">{reportBaseline.tenant_leaks}</span> ·
                dead <span className="font-mono">{reportBaseline.dead_company_refs}</span> ·
                broken FK <span className="font-mono">{reportBaseline.broken_fk_links}</span>
              </div>
            )}
            {reportDiff && (
              <div data-testid="report-diff" className="space-y-3">
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
                  {[
                    ['Health', 'system_health', '%'],
                    ['Hidden', 'hidden_records', ''],
                    ['Tenant Leaks', 'tenant_leaks', ''],
                    ['Dead Co.', 'dead_company', ''],
                    ['Broken FK', 'broken_fk', ''],
                  ].map(([label, key, suffix]) => {
                    const b = key === 'system_health' ? reportDiff.before.system_health
                            : key === 'hidden_records' ? reportDiff.before.hidden_records
                            : key === 'tenant_leaks' ? reportDiff.before.tenant_leaks
                            : key === 'dead_company' ? reportDiff.before.dead_company_refs
                            : reportDiff.before.broken_fk_links;
                    const a = key === 'system_health' ? reportDiff.after.system_health
                            : key === 'hidden_records' ? reportDiff.after.hidden_records
                            : key === 'tenant_leaks' ? reportDiff.after.tenant_leaks
                            : key === 'dead_company' ? reportDiff.after.dead_company_refs
                            : reportDiff.after.broken_fk_links;
                    const delta = reportDiff.delta[key];
                    const good = key === 'system_health' ? delta > 0 : delta < 0;
                    return (
                      <div key={key} className="rounded border border-slate-800 p-2 bg-slate-900/40">
                        <div className="text-[10px] uppercase opacity-60">{label}</div>
                        <div className="font-mono">
                          <span className="text-slate-400">{b}{suffix}</span>
                          <span className="mx-1 text-slate-600">→</span>
                          <span className="text-emerald-300">{a}{suffix}</span>
                        </div>
                        <div className={`text-[10px] font-mono ${good ? 'text-emerald-300' : delta === 0 ? 'text-slate-400' : 'text-rose-300'}`}>
                          {delta > 0 ? '+' : ''}{delta}{suffix}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <div className="text-xs uppercase text-emerald-300 mb-1">Top 10 improved</div>
                    <table className="w-full text-xs">
                      <tbody>
                        {(reportDiff.top_10_improved || []).slice(0, 10).map(p => (
                          <tr key={p.page_id} className="border-t border-slate-800/40">
                            <td className="py-1">{p.label}</td>
                            <td className="py-1 text-right font-mono text-slate-400">{p.health_before}%</td>
                            <td className="py-1 text-center">→</td>
                            <td className="py-1 text-right font-mono text-emerald-300">{p.health_after}%</td>
                            <td className="py-1 text-right font-mono text-emerald-300">+{p.health_delta}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div>
                    <div className="text-xs uppercase text-rose-300 mb-1">Top 10 still suffering</div>
                    <table className="w-full text-xs">
                      <tbody>
                        {(reportDiff.top_10_suffering || []).slice(0, 10).map(p => (
                          <tr key={p.page_id} className="border-t border-slate-800/40">
                            <td className="py-1">{p.label}</td>
                            <td className="py-1 text-right font-mono text-rose-300">{p.hidden_after} hidden</td>
                            <td className="py-1 text-right font-mono">{p.health_after}%</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Past runs */}
          <div className="rounded border border-slate-800 overflow-hidden">
            <div className="bg-slate-900 px-4 py-2 text-sm flex items-center gap-2">
              <History size={14} /> Past recovery runs / السجل
            </div>
            {pastRuns.length === 0 ? (
              <div className="p-6 text-center text-slate-500 text-sm">
                No recovery runs yet. The Preview buttons above are read-only.
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-slate-900/40 text-xs uppercase text-slate-400">
                  <tr>
                    <th className="px-3 py-2 text-left">Run ID</th>
                    <th className="px-3 py-2 text-left">Type</th>
                    <th className="px-3 py-2 text-left">By</th>
                    <th className="px-3 py-2 text-left">Started</th>
                    <th className="px-3 py-2 text-right">Changed</th>
                    <th className="px-3 py-2 text-center">Status</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {pastRuns.map(r => (
                    <tr key={r.id} data-testid={`past-run-${r.id.slice(0,8)}`}
                      className="border-t border-slate-800/50">
                      <td className="px-3 py-2 font-mono text-xs">{r.id.slice(0, 8)}…</td>
                      <td className="px-3 py-2 text-xs">{r.recovery_type}</td>
                      <td className="px-3 py-2 text-xs">{r.executed_by_name}</td>
                      <td className="px-3 py-2 text-xs">{(r.started_at || '').slice(0, 19)}</td>
                      <td className="px-3 py-2 text-right font-mono text-emerald-300">{r.records_changed}</td>
                      <td className="px-3 py-2 text-center">
                        {r.rolled_back ? (
                          <span className={`px-2 py-0.5 rounded text-xs border ${COLORS.amber}`}>Rolled back</span>
                        ) : (
                          <span className={`px-2 py-0.5 rounded text-xs border ${COLORS.emerald}`}>Active</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {!r.rolled_back && (
                          <button data-testid={`rollback-${r.id.slice(0,8)}`}
                            onClick={() => rollbackRun(r.id)}
                            className="px-2 py-1 text-xs bg-amber-500/10 border border-amber-500/30 rounded text-amber-300 hover:bg-amber-500/20 flex items-center gap-1">
                            <RotateCcw size={12} /> Rollback
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* Recovery Preview / Execute modal */}
      {recoveryPreview && (
        <div data-testid="recovery-modal"
          className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => !recoveryRunning && setRecoveryPreview(null)}>
          <div className="bg-slate-900 border border-slate-700 rounded-lg max-w-4xl w-full max-h-[85vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}>
            {recoveryPreview === 'loading' ? (
              <div className="p-12 text-center text-slate-400">
                <Loader2 className="inline animate-spin mr-2" /> Building plan…
              </div>
            ) : (
              <div className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h2 className="text-xl font-bold flex items-center gap-2">
                      <Wrench size={20} className="text-cyan-400" />
                      {recoveryResult ? 'Recovery Complete' : 'Recovery Preview'}
                    </h2>
                    <p className="text-sm text-slate-400 mt-1">
                      Type: <code>{recoveryPreview.recovery_type}</code>
                    </p>
                  </div>
                  <button data-testid="close-recovery-modal"
                    onClick={() => { setRecoveryPreview(null); setRecoveryResult(null); }}
                    disabled={recoveryRunning}
                    className="text-slate-400 hover:text-slate-200 text-2xl leading-none disabled:opacity-30">×</button>
                </div>

                {/* Recovery RESULT view (after execute) */}
                {recoveryResult ? (
                  <div data-testid="recovery-result" className="space-y-4">
                    <div className="rounded border border-emerald-500/30 bg-emerald-500/5 p-4 flex items-start gap-3">
                      <CheckCircle2 className="text-emerald-400 mt-0.5" />
                      <div className="text-sm">
                        <div className="font-semibold text-emerald-300">
                          {recoveryResult.run.records_changed} records updated · run {recoveryResult.run.id.slice(0,8)}…
                        </div>
                        <div className="text-xs text-slate-400 mt-1">
                          {recoveryResult.run.skipped_low_confidence} skipped (low confidence).
                          Full audit trail is in <code>dvl_recovery_history</code>.
                        </div>
                      </div>
                    </div>

                    {/* Before/After diff */}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="rounded border border-slate-800 bg-slate-900/40 p-3">
                        <div className="text-xs uppercase text-slate-500">Before / قبل</div>
                        <div className="text-xs text-slate-300 mt-1">
                          Hidden: <span className="font-mono">{snapshotBefore?.hidden_records?.toLocaleString()}</span>
                        </div>
                        <div className="text-xs text-slate-300">
                          Health: <span className="font-mono">{snapshotBefore?.health_score}%</span>
                        </div>
                      </div>
                      <div className="rounded border border-emerald-500/30 bg-emerald-500/10 p-3">
                        <div className="text-xs uppercase text-emerald-300">After / بعد</div>
                        <div className="text-xs text-slate-300 mt-1">
                          Hidden: <span className="font-mono">{recoveryResult.after?.hidden_records?.toLocaleString()}</span>
                          <span className="ml-2 text-emerald-300 font-mono">
                            −{((snapshotBefore?.hidden_records || 0) - (recoveryResult.after?.hidden_records || 0)).toLocaleString()}
                          </span>
                        </div>
                        <div className="text-xs text-slate-300">
                          Health: <span className="font-mono">{recoveryResult.after?.health_score}%</span>
                          <span className="ml-2 text-emerald-300 font-mono">
                            +{(recoveryResult.after?.health_score || 0) - (snapshotBefore?.health_score || 0)}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div>
                      <div className="text-xs uppercase text-slate-400 mb-1">By collection</div>
                      <table className="w-full text-xs">
                        <thead className="text-slate-500 bg-slate-900/40">
                          <tr><th className="px-2 py-1 text-left">Collection</th><th className="px-2 py-1 text-right">Changed</th></tr>
                        </thead>
                        <tbody>
                          {Object.entries(recoveryResult.run.by_collection || {}).map(([k, v]) => (
                            <tr key={k} className="border-t border-slate-800/50">
                              <td className="px-2 py-1 font-mono">{k}</td>
                              <td className="px-2 py-1 text-right font-mono text-emerald-300">{v}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : (
                  /* PREVIEW view */
                  <div className="space-y-4">
                    <div className="rounded border border-amber-500/30 bg-amber-500/5 p-3 text-sm text-amber-200">
                      <div className="flex items-center gap-4 flex-wrap">
                        <span><strong className="text-emerald-300">{recoveryPreview.total_recoverable ?? recoveryPreview.total_affected}</strong> will be recovered</span>
                        {recoveryPreview.total_abstained > 0 && (
                          <span className="text-amber-300">
                            <strong>{recoveryPreview.total_abstained}</strong> abstained (no evidence — protected)
                          </span>
                        )}
                        <span className="text-slate-400 text-xs">
                          out of {recoveryPreview.total_affected} affected
                        </span>
                      </div>
                      <span className="block text-xs mt-2 text-amber-200/70">
                        {recoveryPreview.note}
                      </span>
                      {recoveryPreview.excluded_snapshots?.length > 0 && (
                        <div className="text-xs mt-1 text-slate-400">
                          Excluded snapshots (rebuild via /api/projections/rebuild instead):
                          <span className="ml-1 font-mono">{recoveryPreview.excluded_snapshots.join(', ')}</span>
                        </div>
                      )}
                    </div>

                    <div className="rounded border border-slate-800 overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-900/60 text-xs uppercase text-slate-400">
                          <tr>
                            <th className="px-3 py-2 text-left">Collection</th>
                            <th className="px-3 py-2 text-right">Affected</th>
                            <th className="px-3 py-2 text-right">Recover</th>
                            <th className="px-3 py-2 text-right">Abstain</th>
                            <th className="px-3 py-2 text-left">Proposed Company</th>
                            <th className="px-3 py-2 text-right">Avg Conf.</th>
                          </tr>
                        </thead>
                        <tbody>
                          {recoveryPreview.collections.map((c, i) => (
                            <tr key={i} data-testid={`preview-row-${c.collection}`}
                              className="border-t border-slate-800/50">
                              <td className="px-3 py-2 font-mono text-xs">{c.collection}</td>
                              <td className="px-3 py-2 text-right font-mono">{c.affected_count}</td>
                              <td className="px-3 py-2 text-right font-mono text-emerald-300">{c.would_recover ?? c.affected_count}</td>
                              <td className="px-3 py-2 text-right font-mono text-amber-300">{c.abstained || 0}</td>
                              <td className="px-3 py-2 font-mono text-xs">{c.proposed_company || '—'}</td>
                              <td className="px-3 py-2 text-right font-mono">
                                {c.avg_confidence > 0 ? (
                                  <span className={`px-1.5 py-0.5 rounded text-[10px] border ${
                                    c.avg_confidence >= 95 ? COLORS.emerald
                                    : c.avg_confidence >= 80 ? COLORS.amber : COLORS.rose
                                  }`}>
                                    {c.avg_confidence}%
                                  </span>
                                ) : <span className="text-slate-500 text-[10px]">—</span>}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <div className="flex items-center gap-4 flex-wrap">
                      <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer">
                        <input data-testid="evidence-required" type="checkbox"
                          checked={evidenceRequired}
                          onChange={async (e) => {
                            setEvidenceRequired(e.target.checked);
                            // Re-fetch preview with new mode
                            setRecoveryPreview('loading');
                            try {
                              const r = await axiosInstance.post(
                                `/legacy-audit/recover/missing-company-id/preview?evidence_required=${e.target.checked}`,
                              );
                              setRecoveryPreview(r.data);
                            } catch (err) {
                              toast.error('Preview reload failed');
                            }
                          }}
                          className="accent-cyan-500" />
                        <span><strong>Evidence-only (strict)</strong> — abstain on records with no traceable FK/user</span>
                      </label>
                      <div className="flex items-center gap-2">
                        <label className="text-xs text-slate-400">Min confidence:</label>
                        <select data-testid="min-confidence"
                          value={minConfidence} onChange={e => setMinConfidence(Number(e.target.value))}
                          className="bg-slate-800 border border-slate-700 rounded px-2 py-1 text-xs">
                          <option value={100}>100% (FK only)</option>
                          <option value={95}>≥ 95%</option>
                          <option value={80}>≥ 80%</option>
                          <option value={60}>≥ 60% (incl. fallback)</option>
                          <option value={0}>All</option>
                        </select>
                      </div>
                    </div>

                    <div className="flex justify-end gap-2 pt-2 border-t border-slate-800">
                      <button data-testid="cancel-recovery"
                        onClick={() => setRecoveryPreview(null)}
                        disabled={recoveryRunning}
                        className="px-4 py-2 text-sm bg-slate-800 hover:bg-slate-700 rounded border border-slate-700">
                        Cancel
                      </button>
                      <button data-testid="execute-recovery"
                        onClick={executeRecovery}
                        disabled={recoveryRunning || (recoveryPreview.total_recoverable ?? recoveryPreview.total_affected) === 0}
                        className="px-4 py-2 text-sm bg-cyan-500/30 hover:bg-cyan-500/50 rounded border border-cyan-500/50 text-cyan-100 flex items-center gap-2 disabled:opacity-40">
                        {recoveryRunning ? <Loader2 size={14} className="animate-spin" /> : <Wrench size={14} />}
                        {recoveryRunning ? 'Executing…' : `Execute Recovery (${recoveryPreview.total_recoverable ?? recoveryPreview.total_affected})`}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Domain Contract tab — formal semantic definition */}
      {activeTab === 'domain' && domainContract && (
        <div data-testid="domain-tab" className="space-y-4">
          <div className="rounded border border-violet-500/30 bg-violet-500/5 p-4">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <BookOpen className="text-violet-400" size={20} />
              Domain Contract — Single Source of Semantic Truth
            </h3>
            <p className="text-xs text-slate-400 mt-2 max-w-3xl">
              The formal definition of approval state, view layer, and action
              rules. No code may write its own variant — every router, every
              UI permission check, every report must derive from these matrices.
            </p>
            <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
              {Object.entries(domainContract.axes).map(([k, v]) => (
                <div key={k} className="rounded border border-slate-800 bg-slate-900/40 p-2">
                  <div className="text-violet-300 font-semibold">{k}</div>
                  <div className="text-slate-400 mt-1">{v}</div>
                </div>
              ))}
            </div>
          </div>

          {/* States with transitions */}
          <div className="rounded border border-slate-800 overflow-hidden">
            <div className="bg-slate-900 px-4 py-2 text-sm font-medium">
              Approval States · Legal Transitions
            </div>
            <table className="w-full text-sm">
              <thead className="bg-slate-900/40 text-xs uppercase text-slate-400">
                <tr>
                  <th className="px-3 py-2 text-left">State</th>
                  <th className="px-3 py-2 text-left">Legal next states</th>
                </tr>
              </thead>
              <tbody>
                {(domainContract.states || []).map(s => (
                  <tr key={s} className="border-t border-slate-800/50">
                    <td className="px-3 py-2 font-mono text-xs">{s}</td>
                    <td className="px-3 py-2 text-xs">
                      {(domainContract.legal_transitions[s] || []).length === 0
                        ? <span className="text-rose-300">terminal</span>
                        : domainContract.legal_transitions[s].map(t => (
                            <span key={t} className="inline-block px-1.5 py-0.5 mr-1 rounded text-[10px] border border-slate-700 bg-slate-800">{t}</span>
                          ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* View Layers — visible states */}
          <div className="rounded border border-slate-800 overflow-hidden">
            <div className="bg-slate-900 px-4 py-2 text-sm font-medium">
              View Layers · Visible States
            </div>
            <table className="w-full text-sm">
              <thead className="bg-slate-900/40 text-xs uppercase text-slate-400">
                <tr>
                  <th className="px-3 py-2 text-left">Layer</th>
                  <th className="px-3 py-2 text-left">Shows states</th>
                </tr>
              </thead>
              <tbody>
                {(domainContract.view_layers || []).map(l => (
                  <tr key={l} className="border-t border-slate-800/50">
                    <td className="px-3 py-2 font-mono text-xs">{l}</td>
                    <td className="px-3 py-2 text-xs">
                      {(domainContract.layer_visible_states[l] || []).map(s => (
                        <span key={s} className={`inline-block px-1.5 py-0.5 mr-1 rounded text-[10px] border ${
                          s === 'approved' ? COLORS.emerald
                          : s === 'rejected' || s === 'archived' ? COLORS.rose
                          : COLORS.amber
                        }`}>{s}</span>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Action Matrix */}
          <div className="rounded border border-slate-800 overflow-hidden">
            <div className="bg-slate-900 px-4 py-2 text-sm font-medium">
              Action Matrix — (state × view) → allowed actions
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-900/40 uppercase text-slate-400">
                  <tr>
                    <th className="px-3 py-2 text-left">State \ Layer</th>
                    {(domainContract.view_layers || []).map(l => (
                      <th key={l} className="px-3 py-2 text-left">{l}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(domainContract.states || []).map(s => (
                    <tr key={s} className="border-t border-slate-800/50">
                      <td className="px-3 py-2 font-mono">{s}</td>
                      {(domainContract.view_layers || []).map(l => {
                        const acts = (domainContract.action_matrix[s] || {})[l] || [];
                        return (
                          <td key={l} className="px-3 py-2">
                            {acts.length === 0
                              ? <span className="text-slate-600 text-[10px]">—</span>
                              : acts.map(a => (
                                  <span key={a} className="inline-block px-1 py-0.5 mr-0.5 mb-0.5 rounded text-[9px] border border-slate-700 bg-slate-800/60">{a}</span>
                                ))}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Approval tab — Hidden-by-Approval Scanner */}
      {activeTab === 'approval' && approvalScan && (
        <div data-testid="approval-tab" className="space-y-4">
          <div className="rounded border border-rose-500/30 bg-rose-500/5 p-4">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <ShieldAlert className="text-rose-400" size={20} />
              Hidden by Approval Filter
              <span className="text-sm text-slate-400 font-normal" dir="rtl">/ مخفي بفلتر الموافقة</span>
            </h3>
            <div className="mt-3 text-sm">
              <span><strong className="text-rose-300">{approvalScan.summary.total_hidden_by_approval}</strong> records hidden</span>
              <span className="mx-3 text-slate-600">·</span>
              <span><strong className="text-emerald-300">{approvalScan.summary.total_approved}</strong> approved</span>
              <span className="mx-3 text-slate-600">·</span>
              <span><strong className="text-amber-300">{approvalScan.summary.total_pending}</strong> pending</span>
              <span className="mx-3 text-slate-600">·</span>
              <span>{approvalScan.summary.collections_affected} affected / {approvalScan.summary.collections_scanned} scanned</span>
              <span className="mx-3 text-slate-600">·</span>
              <span className={approvalScan.summary.pipeline_alive ? 'text-emerald-300' : 'text-rose-300'}>
                pipeline {approvalScan.summary.pipeline_alive ? 'ALIVE' : 'NEVER RAN'}
              </span>
            </div>
            <div className="mt-3 p-3 rounded border border-amber-500/30 bg-amber-500/5 text-xs text-amber-200">
              <strong className="text-amber-300">🔬 Diagnosis:</strong> {approvalScan.summary.diagnosis}
            </div>

            {/* Phase 3.2 — Auto-Approve Legacy backfill */}
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <button data-testid="auto-approve-preview-btn"
                onClick={openAutoApprovePreview}
                disabled={recoveryRunning}
                className="px-3 py-1.5 text-xs bg-emerald-500/20 hover:bg-emerald-500/40 border border-emerald-500/40 rounded text-emerald-200 flex items-center gap-1.5 disabled:opacity-40">
                <CheckCheck size={14} />
                Auto-Approve Legacy Records (preview)
              </button>
              <span className="text-[11px] text-slate-500">
                Backfills approval_status=approved on rows with null/missing/draft/pending/legacy state · ignores rejected · rollback ready.
              </span>
            </div>
          </div>

          {/* Top affected */}
          <div className="rounded border border-slate-800 overflow-hidden">
            <div className="bg-slate-900 px-4 py-2 text-sm font-medium">
              Top affected collections / المجموعات الأكثر تأثراً
            </div>
            <table className="w-full text-sm">
              <thead className="bg-slate-900/40 text-xs uppercase text-slate-400">
                <tr>
                  <th className="px-3 py-2 text-left">Collection</th>
                  <th className="px-3 py-2 text-right">Total</th>
                  <th className="px-3 py-2 text-right">Approved</th>
                  <th className="px-3 py-2 text-right">Pending</th>
                  <th className="px-3 py-2 text-right">Hidden</th>
                  <th className="px-3 py-2 text-center">API Filter</th>
                  <th className="px-3 py-2 text-left">Router</th>
                  <th className="px-3 py-2 text-center">Signal</th>
                </tr>
              </thead>
              <tbody>
                {(approvalScan.top_affected || []).map((r, i) => (
                  <tr key={i} data-testid={`approval-row-${r.collection}`}
                    className="border-t border-slate-800/50">
                    <td className="px-3 py-2 font-mono text-xs">{r.collection}</td>
                    <td className="px-3 py-2 text-right font-mono">{r.total}</td>
                    <td className="px-3 py-2 text-right font-mono text-emerald-300">{r.approved}</td>
                    <td className="px-3 py-2 text-right font-mono text-amber-300">{r.pending}</td>
                    <td className="px-3 py-2 text-right font-mono text-rose-300">{r.hidden_by_approval}</td>
                    <td className="px-3 py-2 text-center">
                      {r.api_filters_approved ? <span className={`px-1.5 py-0.5 rounded text-[10px] border ${COLORS.rose}`}>YES</span> : <span className="text-slate-500 text-xs">no</span>}
                    </td>
                    <td className="px-3 py-2 text-xs font-mono text-slate-400">
                      {r.api_router ? `${r.api_router}:${r.api_filter_line}` : '—'}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <span className={`px-1.5 py-0.5 rounded text-[10px] border ${
                        r.health_signal === 'HEALTHY' ? COLORS.emerald
                        : r.health_signal === 'CRITICAL_PENDING_LOCK' ? COLORS.rose
                        : r.health_signal === 'DEGRADED' ? COLORS.amber
                        : COLORS.slate
                      }`}>{r.health_signal}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* All routers enforcing the filter */}
          <div className="rounded border border-slate-800 overflow-hidden">
            <div className="bg-slate-900 px-4 py-2 text-sm font-medium">
              All routers enforcing approved-only filter ({approvalScan.routers_filtering_approved?.length || 0})
            </div>
            <table className="w-full text-xs">
              <thead className="bg-slate-900/40 uppercase text-slate-400">
                <tr><th className="px-3 py-1.5 text-left">Collection</th><th className="px-3 py-1.5 text-left">Router file</th><th className="px-3 py-1.5 text-right">Line</th></tr>
              </thead>
              <tbody>
                {(approvalScan.routers_filtering_approved || []).map((r, i) => (
                  <tr key={i} className="border-t border-slate-800/40">
                    <td className="px-3 py-1.5 font-mono">{r.collection}</td>
                    <td className="px-3 py-1.5 font-mono">{r.router}</td>
                    <td className="px-3 py-1.5 text-right font-mono">{r.line}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Phase 1C — Dead Company preview modal */}
      {deadCompanyPreview && (
        <div data-testid="dead-company-modal"
          className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => !recoveryRunning && setDeadCompanyPreview(null)}>
          <div className="bg-slate-900 border border-slate-700 rounded-lg max-w-4xl w-full max-h-[85vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}>
            {deadCompanyPreview === 'loading' ? (
              <div className="p-12 text-center text-slate-400"><Loader2 className="inline animate-spin mr-2" /> Loading…</div>
            ) : (
              <div className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h2 className="text-xl font-bold">Dead Company References</h2>
                    <p className="text-xs text-slate-400 mt-1">{deadCompanyPreview.note}</p>
                  </div>
                  <button onClick={() => setDeadCompanyPreview(null)} className="text-slate-400 text-2xl leading-none">×</button>
                </div>
                <div className="rounded border border-fuchsia-500/30 bg-fuchsia-500/5 p-3 text-sm mb-3">
                  <span><strong className="text-emerald-300">{deadCompanyPreview.total_auto_recoverable}</strong> auto-recoverable (Class A+/A)</span>
                  <span className="ml-4 text-amber-300"><strong>{deadCompanyPreview.total_manual_review}</strong> need manual review</span>
                  <span className="ml-4 text-slate-400 text-xs">of {deadCompanyPreview.total_affected} affected</span>
                </div>
                {deadCompanyPreview.collections.length === 0 ? (
                  <div className="text-emerald-300 text-sm">✓ No dead-company references found — every company_id points to a live tenant.</div>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-slate-900/60 text-xs uppercase text-slate-400">
                      <tr>
                        <th className="px-3 py-2 text-left">Collection</th>
                        <th className="px-3 py-2 text-right">Affected</th>
                        <th className="px-3 py-2 text-right">Auto</th>
                        <th className="px-3 py-2 text-right">Manual</th>
                        <th className="px-3 py-2 text-center">A+</th>
                        <th className="px-3 py-2 text-center">A</th>
                        <th className="px-3 py-2 text-center">B</th>
                        <th className="px-3 py-2 text-center">C</th>
                        <th className="px-3 py-2 text-center">Unk</th>
                      </tr>
                    </thead>
                    <tbody>
                      {deadCompanyPreview.collections.map((c, i) => (
                        <tr key={i} className="border-t border-slate-800/50">
                          <td className="px-3 py-2 font-mono text-xs">{c.collection}</td>
                          <td className="px-3 py-2 text-right font-mono">{c.affected_count}</td>
                          <td className="px-3 py-2 text-right font-mono text-emerald-300">{c.would_recover_auto}</td>
                          <td className="px-3 py-2 text-right font-mono text-amber-300">{c.needs_manual_review}</td>
                          <td className="px-3 py-2 text-center font-mono text-xs">{c.class_counts['A+']}</td>
                          <td className="px-3 py-2 text-center font-mono text-xs">{c.class_counts['A']}</td>
                          <td className="px-3 py-2 text-center font-mono text-xs">{c.class_counts['B']}</td>
                          <td className="px-3 py-2 text-center font-mono text-xs">{c.class_counts['C']}</td>
                          <td className="px-3 py-2 text-center font-mono text-xs">{c.class_counts['Unknown']}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                <div className="flex justify-end gap-2 pt-4 border-t border-slate-800 mt-4">
                  <button onClick={() => setDeadCompanyPreview(null)}
                    className="px-4 py-2 text-sm bg-slate-800 hover:bg-slate-700 rounded border border-slate-700">Cancel</button>
                  <button data-testid="execute-dead-company" onClick={executeDeadCompany}
                    disabled={recoveryRunning || deadCompanyPreview.total_auto_recoverable === 0}
                    className="px-4 py-2 text-sm bg-fuchsia-500/30 hover:bg-fuchsia-500/50 rounded border border-fuchsia-500/50 text-fuchsia-100 flex items-center gap-2 disabled:opacity-40">
                    {recoveryRunning ? <Loader2 size={14} className="animate-spin" /> : <Wrench size={14} />}
                    Execute (A+/A only) — {deadCompanyPreview.total_auto_recoverable}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Phase 1D — Broken FK preview modal */}
      {brokenFkPreview && (
        <div data-testid="broken-fk-modal"
          className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => !recoveryRunning && setBrokenFkPreview(null)}>
          <div className="bg-slate-900 border border-slate-700 rounded-lg max-w-4xl w-full max-h-[85vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}>
            {brokenFkPreview === 'loading' ? (
              <div className="p-12 text-center text-slate-400"><Loader2 className="inline animate-spin mr-2" /> Loading…</div>
            ) : (
              <div className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h2 className="text-xl font-bold">Broken Foreign Keys</h2>
                    <p className="text-xs text-slate-400 mt-1">{brokenFkPreview.note}</p>
                  </div>
                  <button onClick={() => setBrokenFkPreview(null)} className="text-slate-400 text-2xl leading-none">×</button>
                </div>
                <div className="rounded border border-amber-500/30 bg-amber-500/5 p-3 text-sm mb-3">
                  <span><strong className="text-emerald-300">{brokenFkPreview.total_auto_recoverable}</strong> auto-recoverable (Class A+ name match)</span>
                  <span className="ml-4 text-amber-300"><strong>{brokenFkPreview.total_broken_links - brokenFkPreview.total_auto_recoverable}</strong> need manual remedy</span>
                </div>
                {brokenFkPreview.issues.length === 0 ? (
                  <div className="text-emerald-300 text-sm">✓ No broken foreign keys.</div>
                ) : (
                  <div className="space-y-3">
                    {brokenFkPreview.issues.map((iss, i) => (
                      <div key={i} className="rounded border border-slate-800 bg-slate-900/40 p-3">
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="text-sm font-medium">{iss.page_label}</div>
                            <div className="text-xs text-slate-500 font-mono">
                              {iss.collection}.{iss.fk_field} → {iss.target}
                            </div>
                          </div>
                          <div className="text-right text-xs">
                            <div className="font-mono text-rose-300">{iss.broken_count} broken</div>
                            <div className="text-[10px] text-slate-500">
                              A+:{iss.class_counts['A+']} A:{iss.class_counts['A']} B:{iss.class_counts['B']} C:{iss.class_counts['C']} Unk:{iss.class_counts['Unknown']}
                            </div>
                          </div>
                        </div>
                        {iss.samples?.length > 0 && (
                          <details className="mt-2">
                            <summary className="text-xs cursor-pointer text-slate-400 hover:text-slate-200">
                              Show {iss.samples.length} sample remedies
                            </summary>
                            <table className="w-full text-xs mt-2">
                              <thead className="bg-slate-900/40 text-slate-500">
                                <tr>
                                  <th className="px-2 py-1 text-left">id</th>
                                  <th className="px-2 py-1 text-left">current value</th>
                                  <th className="px-2 py-1 text-left">remedy</th>
                                  <th className="px-2 py-1 text-center">class</th>
                                </tr>
                              </thead>
                              <tbody>
                                {iss.samples.map((s, j) => (
                                  <tr key={j} className="border-t border-slate-800/30">
                                    <td className="px-2 py-1 font-mono">{(s.id || '').slice(0, 10)}…</td>
                                    <td className="px-2 py-1 font-mono">{s.current_value?.slice(0, 30)}</td>
                                    <td className="px-2 py-1">{s.remedy}</td>
                                    <td className="px-2 py-1 text-center">
                                      <span className={`px-1.5 py-0.5 rounded text-[10px] border ${
                                        s.confidence_class === 'A+' ? COLORS.emerald
                                        : s.confidence_class === 'A' ? COLORS.emerald
                                        : s.confidence_class === 'B' ? COLORS.amber : COLORS.rose
                                      }`}>{s.confidence_class}</span>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </details>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex justify-end gap-2 pt-4 border-t border-slate-800 mt-4">
                  <button onClick={() => setBrokenFkPreview(null)}
                    className="px-4 py-2 text-sm bg-slate-800 hover:bg-slate-700 rounded border border-slate-700">Cancel</button>
                  <button data-testid="quarantine-class-b" onClick={quarantineClassB}
                    disabled={recoveryRunning || brokenFkPreview.total_broken_links === brokenFkPreview.total_auto_recoverable}
                    title="Mark Class B broken-FK records as quarantined. Excluded from BUSINESS queries. Fully reversible."
                    className="px-4 py-2 text-sm bg-purple-500/30 hover:bg-purple-500/50 rounded border border-purple-500/50 text-purple-100 flex items-center gap-2 disabled:opacity-40">
                    {recoveryRunning ? <Loader2 size={14} className="animate-spin" /> : <ShieldOff size={14} />}
                    Quarantine Class B — {brokenFkPreview.total_broken_links - brokenFkPreview.total_auto_recoverable}
                  </button>
                  <button data-testid="execute-broken-fk" onClick={executeBrokenFk}
                    disabled={recoveryRunning || brokenFkPreview.total_auto_recoverable === 0}
                    className="px-4 py-2 text-sm bg-amber-500/30 hover:bg-amber-500/50 rounded border border-amber-500/50 text-amber-100 flex items-center gap-2 disabled:opacity-40">
                    {recoveryRunning ? <Loader2 size={14} className="animate-spin" /> : <Wrench size={14} />}
                    Execute (A+ only) — {brokenFkPreview.total_auto_recoverable}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Phase 3.2 — Auto-Approve Legacy preview modal */}
      {autoApprovePreview && (
        <div data-testid="auto-approve-modal"
          className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => !recoveryRunning && setAutoApprovePreview(null)}>
          <div className="bg-slate-900 border border-slate-700 rounded-lg max-w-3xl w-full max-h-[85vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}>
            {autoApprovePreview === 'loading' ? (
              <div className="p-12 text-center text-slate-400"><Loader2 className="inline animate-spin mr-2" /> Loading…</div>
            ) : (
              <div className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h2 className="text-xl font-bold flex items-center gap-2">
                      <CheckCheck className="text-emerald-400" size={20} />
                      Auto-Approve Legacy Records
                    </h2>
                    <p className="text-xs text-slate-400 mt-1">
                      Backfills approval_status=approved on rows that pre-date the workflow. Rejected rows are never touched.
                    </p>
                  </div>
                  <button onClick={() => setAutoApprovePreview(null)} className="text-slate-400 text-2xl leading-none">×</button>
                </div>
                <div className="rounded border border-emerald-500/30 bg-emerald-500/5 p-3 text-sm mb-3 flex flex-wrap gap-x-4 gap-y-1">
                  <span><strong className="text-emerald-300">{autoApprovePreview.total_eligible}</strong> eligible</span>
                  <span className="text-amber-300"><strong>{autoApprovePreview.total_skipped_young}</strong> skipped (too young, &lt; {autoApprovePreview.min_age_days}d)</span>
                  <span className="text-rose-300"><strong>{autoApprovePreview.total_skipped_rejected}</strong> rejected (untouched)</span>
                </div>
                {autoApprovePreview.collections.length === 0 || autoApprovePreview.total_eligible === 0 ? (
                  <div className="text-emerald-300 text-sm">✓ No legacy approval records.</div>
                ) : (
                  <div className="space-y-3">
                    {autoApprovePreview.collections.filter(c => c.eligible > 0).map((c) => (
                      <div key={c.collection} className="rounded border border-slate-800 bg-slate-900/40 p-3">
                        <div className="flex justify-between items-center">
                          <div className="font-mono text-sm font-medium text-slate-200">{c.collection}</div>
                          <div className="text-right text-xs">
                            <span className="font-mono text-emerald-300">{c.eligible} eligible</span>
                            {c.skipped_young > 0 && (
                              <span className="ml-2 font-mono text-amber-300">·  {c.skipped_young} too young</span>
                            )}
                          </div>
                        </div>
                        {c.samples?.length > 0 && (
                          <details className="mt-2">
                            <summary className="text-xs cursor-pointer text-slate-400 hover:text-slate-200">
                              Show {c.samples.length} sample{c.samples.length === 1 ? '' : 's'}
                            </summary>
                            <table className="w-full text-xs mt-2">
                              <thead className="bg-slate-900/40 text-slate-500">
                                <tr>
                                  <th className="px-2 py-1 text-left">id</th>
                                  <th className="px-2 py-1 text-left">label</th>
                                  <th className="px-2 py-1 text-left">current status</th>
                                  <th className="px-2 py-1 text-left">created at</th>
                                </tr>
                              </thead>
                              <tbody>
                                {c.samples.map((s, j) => (
                                  <tr key={j} className="border-t border-slate-800/30">
                                    <td className="px-2 py-1 font-mono">{(s.id || '').slice(0, 10)}…</td>
                                    <td className="px-2 py-1">{s.label || '—'}</td>
                                    <td className="px-2 py-1 font-mono">{s.current_status === null ? 'null' : s.current_status || '∅'}</td>
                                    <td className="px-2 py-1 font-mono text-slate-500">{(s.created_at || '').slice(0, 10)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </details>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex justify-end gap-2 pt-4 border-t border-slate-800 mt-4">
                  <button onClick={() => setAutoApprovePreview(null)}
                    className="px-4 py-2 text-sm bg-slate-800 hover:bg-slate-700 rounded border border-slate-700">Cancel</button>
                  <button data-testid="execute-auto-approve" onClick={executeAutoApprove}
                    disabled={recoveryRunning || (autoApprovePreview.total_eligible || 0) === 0}
                    className="px-4 py-2 text-sm bg-emerald-500/30 hover:bg-emerald-500/50 rounded border border-emerald-500/50 text-emerald-100 flex items-center gap-2 disabled:opacity-40">
                    {recoveryRunning ? <Loader2 size={14} className="animate-spin" /> : <CheckCheck size={14} />}
                    Execute — {autoApprovePreview.total_eligible}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}


      {/* Detail modal */}
      {selectedPage && (
        <div data-testid="page-detail-modal"
          className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => { setSelectedPage(null); setDetail(null); }}>
          <div className="bg-slate-900 border border-slate-700 rounded-lg max-w-5xl w-full max-h-[85vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}>
            {!detail ? (
              <div className="p-12 text-center text-slate-400">
                <Loader2 className="inline animate-spin mr-2" /> Loading…
              </div>
            ) : (
              <div className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h2 className="text-xl font-bold">{detail.page_label}</h2>
                    <p className="text-sm text-slate-400" dir="rtl">{detail.page_label_ar}</p>
                    <p className="text-xs font-mono text-slate-500 mt-1">
                      {detail.collection} · {detail.api_route}
                    </p>
                  </div>
                  <button data-testid="close-detail" onClick={() => { setSelectedPage(null); setDetail(null); }}
                    className="text-slate-400 hover:text-slate-200">×</button>
                </div>

                <div className="grid grid-cols-4 gap-3 mb-4">
                  <StatCard label="Total" label_ar="إجمالي" value={detail.total_records}
                    accent="slate" />
                  <StatCard label="Visible" label_ar="ظاهر" value={detail.visible_records}
                    accent="emerald" />
                  <StatCard label="Hidden" label_ar="مفقود" value={detail.hidden_records}
                    accent="rose" />
                  <StatCard label="Health" label_ar="الصحة" value={`${detail.health_score}%`}
                    accent={detail.health_score >= 95 ? 'emerald' : detail.health_score >= 70 ? 'amber' : 'rose'} />
                </div>

                <h3 className="text-sm uppercase text-slate-400 mb-2">Hidden breakdown / تفاصيل المفقود</h3>
                {Object.entries(detail.hidden_breakdown).filter(([, n]) => n > 0).length === 0 ? (
                  <div className="text-emerald-300 text-sm">
                    ✓ لا توجد سجلات مخفية في هذه الصفحة / No hidden records on this page.
                  </div>
                ) : Object.entries(detail.hidden_breakdown).filter(([, n]) => n > 0).map(([reason, count]) => (
                  <div key={reason} className="border border-slate-800 rounded mb-3">
                    <div className="bg-slate-900/60 px-3 py-2 flex justify-between items-center">
                      <div className="text-sm">
                        <span className="font-medium text-rose-300">{REASON_LABELS[reason]?.en || reason}</span>
                        <span className="text-slate-500 mx-2">·</span>
                        <span dir="rtl">{REASON_LABELS[reason]?.ar}</span>
                      </div>
                      <div className="font-mono text-rose-300">{count}</div>
                    </div>
                    {detail.samples?.[reason]?.length > 0 && (
                      <table className="w-full text-xs">
                        <thead className="bg-slate-900/30 text-slate-500">
                          <tr>
                            <th className="px-3 py-1.5 text-left">ID</th>
                            <th className="px-3 py-1.5 text-left">company_id</th>
                            <th className="px-3 py-1.5 text-left">Name / Label</th>
                            <th className="px-3 py-1.5 text-left">Reason</th>
                          </tr>
                        </thead>
                        <tbody>
                          {detail.samples[reason].map((s, i) => (
                            <tr key={i} className="border-t border-slate-800/40">
                              <td className="px-3 py-1.5 font-mono">{(s.id || '—').slice(0, 12)}</td>
                              <td className="px-3 py-1.5 font-mono">{s.company_id || '∅'}</td>
                              <td className="px-3 py-1.5">{s.name || '—'}</td>
                              <td className="px-3 py-1.5 text-amber-300/80">{s.reason}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                ))}

                <div className="mt-4 p-3 rounded border border-amber-500/30 bg-amber-500/5 text-xs text-amber-200">
                  <AlertTriangle size={14} className="inline mr-1" />
                  <strong>Read-only mode.</strong> Recovery actions (re-link FK, restore company_id,
                  un-archive) will live on this page in Phase 1 of the program — each with a preview
                  before apply.
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LegacyRecoveryCenter;
